import { MessageFilled } from "@ant-design/icons";
import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import {
    AssortmentTaskType,
    Market,
    SubtaskFinGroupPhotos,
    TaskAssortmentMatrix,
    TaskStatus,
} from "tm-shared/types";

import { BottomSheet } from "@/components/BottomSheet";
import { StatusCycleButton } from "@/components/CycleButton";
import FlexContainer from "@/components/FlexContainer";
import { GalleryUploader } from "@/components/GalleryUploader";
import { OutlineIconButton } from "@/components/OutlineIconButton";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useActiveScrollNavigator } from "@/hooks/useActiveScrollNavigator";
import { useCheckCommentOnReject } from "@/hooks/useCheckCommentOnReject";
import { useMarketDistance } from "@/hooks/useMarketDistance";
import { useStickyFilteredItems } from "@/hooks/useStickyFilteredItems";
import {
    useSubtaskConfigSelector,
    useSubtaskFinGroupPhotosSelector,
} from "@/hooks/useTaskSelectors";
import {
    mutateAssortmentFinGroupPhotosEntityPhotos,
    patchAssortmentFinGroupPhotosEntity,
} from "@/store/slices/progress";
import { setMdTasksConfig } from "@/store/slices/ui";
import { addModalToQueue } from "@/store/slices/ui";
import { ModalType } from "@/types/modals";
import { buildPhotoCaptions } from "@/utils/photo/captions";

import { useAssortmentMatrixCompletion } from "../hooks/useAssortmentMatrixCompletion";
import { StepButton } from "../StepButton";
import { StyledList } from "../Styled";
import { CameraPreviewOverlay } from "./components/CameraPreviewOverlay";
import { TaskFinGroupPhotos } from "./components/TaskFinGroupPhotos";
import { StyledBottomSheetTop, StyledMainContainer } from "./Styled";

interface FinGroupPhotosProps {
    subtask: SubtaskFinGroupPhotos;
    market: Market;
    task: TaskAssortmentMatrix;
}

export const FinGroupPhotos: FC<FinGroupPhotosProps> = ({
    subtask,
    market,
    task,
}) => {
    const dispatch = useAppDispatch();

    const marketId = market.id;
    const taskId = task.id;
    const contractId = subtask.contractId;
    const subtaskId = subtask.subtaskId;

    const { firstName, lastName } = useAppSelector((s) => s.user);

    const subtaskPayload = useSubtaskFinGroupPhotosSelector({
        taskId,
        subtaskId,
    });

    const { activeItem, showUncompleted } = useSubtaskConfigSelector({
        marketId,
        taskId,
        subtaskId,
    });

    const photoCategories = useMemo(
        () => Array.from(new Set(subtask.context.photos)),
        [subtask.context.photos],
    );

    const activePayload = subtaskPayload?.tasks?.[activeItem];
    const activeStatus = activePayload?.status ?? TaskStatus.Incomplete;

    const prevRefId = useRef<string | null>(null);

    const resetStatusIfNeeded = useCallback(
        (id: string) => {
            const payload = subtaskPayload?.tasks?.[id];

            if (!payload) return;

            if (payload.status === TaskStatus.Rejected && !payload.comment) {
                dispatch(
                    patchAssortmentFinGroupPhotosEntity({
                        taskId,
                        contractId,
                        id,
                        subtaskId,
                        patch: { status: TaskStatus.Incomplete },
                    }),
                );
            }
        },
        [taskId, contractId, activeItem, subtaskId],
    );

    useEffect(() => {
        const prev = prevRefId.current;

        if (prev && activeItem !== prev) {
            resetStatusIfNeeded(prev);
        }

        prevRefId.current = activeItem ?? null;
    }, [activeItem, resetStatusIfNeeded]);

    const onChangeTaskStatus = useCallback(
        (value: TaskStatus, id?: string) => {
            const targetId = id ?? activeItem;

            if (!targetId) return;

            dispatch(
                patchAssortmentFinGroupPhotosEntity({
                    taskId,
                    contractId,
                    subtaskId,
                    id: targetId,
                    patch: { status: value, comment: "" },
                }),
            );
        },
        [taskId, activeItem, contractId, subtaskId],
    );

    const handlerMessage = useCallback(() => {
        if (activeStatus === TaskStatus.Incomplete || !activeItem) return;

        dispatch(
            addModalToQueue({
                type: ModalType.Comment,
                id: ModalType.Comment,
                payload: {
                    initialComment: activePayload?.comment ?? "",
                    route: AssortmentTaskType.FinGroupPhotos,
                    taskId,
                    contractId,
                    subtaskId,
                    id: activeItem,
                    taskStatus: activeStatus,
                },
            }),
        );
    }, [
        activePayload,
        taskId,
        activeItem,
        contractId,
        activeStatus,
        subtaskId,
    ]);

    const onClickInfoButton = useCallback(() => {
        dispatch(
            addModalToQueue({
                type: ModalType.TaskInfo,
                id: ModalType.TaskInfo,
                payload: {
                    title: "Фото акційних цінників",
                    description:
                        "Зробіть фото акційних цінників для кожного товару. На фото має бути видно товар і його акційну ціну. Якщо фото неможливо зробити — виберіть статус і додайте коментар.",
                },
            }),
        );
    }, []);

    const onChangeActive = useCallback(
        (id: string) => {
            dispatch(
                setMdTasksConfig({
                    marketId,
                    taskId,
                    subtaskId,
                    field: "activeItem",
                    value: id,
                }),
            );
        },
        [marketId, taskId, subtaskId],
    );

    const { filteredItems: filteredTasks } = useStickyFilteredItems({
        items: photoCategories,
        showUncompleted: showUncompleted ?? false,
        getItemId: (category) => category,
        payloadsMap: subtaskPayload?.tasks ?? {},
        isIncomplete: (payload) => {
            return !payload || payload.status === TaskStatus.Incomplete;
        },
    });

    const { validateAndNotify } = useCheckCommentOnReject();

    const handleCheckCommentOnReject = useCallback(
        (category: string) => {
            const previousCategory = prevRefId.current;
            const categoryPayload = subtaskPayload?.tasks?.[previousCategory];

            if (validateAndNotify(categoryPayload)) {
                return;
            }

            onChangeActive(category);
        },
        [subtaskPayload, validateAndNotify],
    );

    const { activeIndex, getItemRef, onNext, onPrev, scrollToActive } =
        useActiveScrollNavigator({
            ids: filteredTasks,
            activeId: activeItem,
            onChangeActive: handleCheckCommentOnReject,
        });

    const onCloseBottomSheet = useCallback(() => {
        dispatch(
            setMdTasksConfig({
                marketId,
                taskId,
                subtaskId,
                field: "activeItem",
                value: null,
            }),
        );
    }, [marketId, taskId, subtaskId]);

    const onDeletePhoto = useCallback(
        (photoId: string) => {
            if (!activeItem) return;

            dispatch(
                mutateAssortmentFinGroupPhotosEntityPhotos({
                    taskId,
                    contractId,
                    id: activeItem,
                    option: "remove",
                    photoId,
                    subtaskId,
                }),
            );
        },
        [taskId, contractId, activeItem, subtaskId],
    );

    const onUploadPhoto = useCallback(
        (photo: string) => {
            if (!activeItem) return;

            dispatch(
                mutateAssortmentFinGroupPhotosEntityPhotos({
                    taskId,
                    contractId,
                    id: activeItem,
                    option: "add",
                    photo,
                    subtaskId,
                }),
            );
        },
        [taskId, contractId, activeItem, subtaskId],
    );

    useAssortmentMatrixCompletion({
        marketId,
        taskId,
        subtaskId,
        expectedCount: photoCategories?.length ?? 0,
        payloadsMap: subtaskPayload?.tasks ?? null,
    });

    const { distance } = useMarketDistance({ marketGeo: market.geo });

    const captions = useMemo(
        () =>
            buildPhotoCaptions({
                marketName: market.name,
                taskName: task.name,
                userFullName: `${firstName} ${lastName}`,
                distance,
            }),
        [distance, firstName, lastName, market.name, task.name],
    );

    useEffect(() => {
        scrollToActive();
    }, [subtaskPayload?.completed]);

    return (
        <>
            <StyledMainContainer>
                <StyledList>
                    {filteredTasks.map((category) => {
                        const isActive = activeItem === category;
                        const currentPayload =
                            subtaskPayload?.tasks?.[category];

                        return (
                            <div key={category} ref={getItemRef(category)}>
                                <TaskFinGroupPhotos
                                    item={category}
                                    isActive={isActive}
                                    onChangeActive={onChangeActive}
                                    payload={currentPayload}
                                    onChangeTaskStatus={onChangeTaskStatus}
                                />
                            </div>
                        );
                    })}
                </StyledList>

                <StepButton
                    task={task}
                    marketId={marketId}
                    currentCompleted={subtaskPayload?.completed}
                />
            </StyledMainContainer>

            <BottomSheet
                open={!!activeItem}
                onClose={onCloseBottomSheet}
                onNext={onNext}
                onPrev={onPrev}
                onClickInfoButton={onClickInfoButton}
            >
                <StyledBottomSheetTop>
                    <PrimaryText>{photoCategories[activeIndex]}</PrimaryText>
                    <FlexContainer gap="20px">
                        <OutlineIconButton
                            icon={<MessageFilled />}
                            onClick={handlerMessage}
                            disabled={activeStatus === TaskStatus.Incomplete}
                        />
                        <StatusCycleButton
                            value={activeStatus}
                            onChange={onChangeTaskStatus}
                            states={[
                                TaskStatus.Incomplete,
                                TaskStatus.Rejected,
                            ]}
                            disabled={activeStatus === TaskStatus.Completed}
                        />
                    </FlexContainer>
                </StyledBottomSheetTop>

                <GalleryUploader
                    previewTitle={filteredTasks[activeIndex]}
                    onDelete={onDeletePhoto}
                    onUpload={onUploadPhoto}
                    activeItemId={activeItem}
                    marketId={marketId}
                    savedPhotos={
                        subtaskPayload?.tasks?.[activeItem]?.photos ?? []
                    }
                    disabled={activeStatus === TaskStatus.Rejected}
                    captionLines={captions}
                    cameraProps={{
                        previewOverlay: (
                            <CameraPreviewOverlay fingroup={activeItem} />
                        ),
                    }}
                />
            </BottomSheet>
        </>
    );
};
